/**
 * Global Error Boundary View
 * Shown when a render error occurs
 */

import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

interface ErrorBoundaryViewProps {
  error: Error;
  reset?: () => void;
}

export function ErrorBoundaryView({ error, reset }: ErrorBoundaryViewProps) {
  const router = useRouter();
  const queryClient = useQueryClient();

  const handleRetry = () => {
    // Clear query cache to allow fresh fetches
    queryClient.clear();
    // Reset error boundary if available
    reset?.();
    // Navigate to trigger re-render
    router.invalidate();
  };

  const isDev = import.meta.env.DEV;

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card text-center">
          {/* Error icon */}
          <svg
            className="mx-auto h-16 w-16 text-warning mb-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>

          <h1 className="text-xl font-semibold text-text mb-2">
            Something went wrong. Please try again.
          </h1>

          {/* Error details in dev mode */}
          {isDev && error && (
            <div className="mb-6 p-3 bg-surface rounded-lg text-left">
              <p className="text-xs font-mono text-warning break-all">{error.message}</p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button onClick={handleRetry} className="btn-primary w-full">
              Try Again
            </button>
            <a
              href="/settings"
              className="text-sm text-primary hover:text-primary/80 transition-colors duration-fast"
            >
              Go to Settings
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
